// TanStack Query Keys Factory
// Using a factory pattern for type-safe query keys

export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.users.details(), id] as const,
  },

  // Clients
  clients: {
    all: ['clients'] as const,
    lists: () => [...queryKeys.clients.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.clients.lists(), filters] as const,
    details: () => [...queryKeys.clients.all, 'detail'] as const,
    detail: (id: number | string) =>
      [...queryKeys.clients.details(), id] as const,
  },

  // Matters
  matters: {
    all: ['matters'] as const,
    lists: () => [...queryKeys.matters.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.matters.lists(), filters] as const,
    details: () => [...queryKeys.matters.all, 'detail'] as const,
    detail: (id: number | string) =>
      [...queryKeys.matters.details(), id] as const,
  },

  // Contracts
  contracts: {
    all: ['contracts'] as const,
    lists: () => [...queryKeys.contracts.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.contracts.lists(), filters] as const,
    details: () => [...queryKeys.contracts.all, 'detail'] as const,
    detail: (id: number | string) =>
      [...queryKeys.contracts.details(), id] as const,
  },

  // Invoices
  invoices: {
    all: ['invoices'] as const,
    lists: () => [...queryKeys.invoices.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.invoices.lists(), filters] as const,
    details: () => [...queryKeys.invoices.all, 'detail'] as const,
    detail: (id: number | string) =>
      [...queryKeys.invoices.details(), id] as const,
  },

  // Time Entries
  timeEntries: {
    all: ['timeEntries'] as const,
    lists: () => [...queryKeys.timeEntries.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.timeEntries.lists(), filters] as const,
    details: () => [...queryKeys.timeEntries.all, 'detail'] as const,
    detail: (id: number | string) =>
      [...queryKeys.timeEntries.details(), id] as const,
  },

  // Events (Calendar)
  events: {
    all: ['events'] as const,
    lists: () => [...queryKeys.events.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.events.lists(), filters] as const,
    details: () => [...queryKeys.events.all, 'detail'] as const,
    detail: (id: number | string) =>
      [...queryKeys.events.details(), id] as const,
  },

  // Calendar
  calendar: {
    all: ['calendar'] as const,
    events: (filters: Record<string, unknown>) =>
      [...queryKeys.calendar.all, 'events', filters] as const,
    event: (id: number | string | undefined) =>
      [...queryKeys.calendar.all, 'event', id] as const,
    users: () => [...queryKeys.calendar.all, 'users'] as const,
    eventTypes: () => [...queryKeys.calendar.all, 'eventTypes'] as const,
  },

  // Reports
  reports: {
    all: ['reports'] as const,
    dashboard: () => [...queryKeys.reports.all, 'dashboard'] as const,
    revenue: (filters: Record<string, unknown>) =>
      [...queryKeys.reports.all, 'revenue', filters] as const,
    matters: (filters: Record<string, unknown>) =>
      [...queryKeys.reports.all, 'matters', filters] as const,
  },

  // Settings
  settings: {
    all: ['settings'] as const,
    company: () => [...queryKeys.settings.all, 'company'] as const,
    billing: () => [...queryKeys.settings.all, 'billing'] as const,
    practiceAreas: () => [...queryKeys.settings.all, 'practiceAreas'] as const,
  },

  // Bills
  bills: {
    all: ['bills'] as const,
    lists: () => [...queryKeys.bills.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.bills.lists(), filters] as const,
    details: () => [...queryKeys.bills.all, 'detail'] as const,
    detail: (id: number | string) =>
      [...queryKeys.bills.details(), id] as const,
  },

  // Payments
  payments: {
    all: ['payments'] as const,
    lists: () => [...queryKeys.payments.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.payments.lists(), filters] as const,
    details: () => [...queryKeys.payments.all, 'detail'] as const,
    detail: (id: number | string) =>
      [...queryKeys.payments.details(), id] as const,
  },

  // Payment Methods
  paymentMethods: {
    all: ['paymentMethods'] as const,
    list: () => [...queryKeys.paymentMethods.all, 'list'] as const,
  },
} as const;
